const API_URL = process.env.REACT_APP_API_URL;

/**
 * Streams a chatbot response from the backend's SSE endpoint
 * (POST /api/chatbot/stream) and calls the provided callbacks as data
 * arrives. Mirrors the mobile app's ChatbotScreen.js streaming logic so
 * both platforms talk to the exact same backend contract.
 *
 * @param {object} params
 * @param {string} params.message - The user's message text
 * @param {string|null} params.sessionId - Existing session id, if any
 * @param {(token: string) => void} params.onToken - Called for each streamed token
 * @param {(sessionId: string) => void} params.onSessionId - Called once the session id is known
 * @param {() => void} [params.onDone] - Called when the stream completes successfully
 * @param {(error: Error) => void} params.onError - Called on any failure
 * @param {AbortSignal} [params.signal] - Optional abort signal to cancel the request
 */
export const streamChatbotMessage = async ({
  message,
  sessionId,
  onToken,
  onSessionId,
  onDone,
  onError,
  signal,
}) => {
  try {
    const res = await fetch(`${API_URL}/chatbot/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message,
        session_id: sessionId || undefined,
      }),
      signal,
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Chatbot request failed (${res.status})${txt ? `: ${txt}` : ''}`);
    }

    const reader = res.body?.getReader?.();
    if (!reader) {
      // Fallback for environments without streaming support — read the whole body at once.
      const raw = await res.text();
      parseSseChunk(raw, { onToken, onSessionId });
      onDone?.();
      return;
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let doneReceived = false;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line.startsWith('data:')) continue;
        const dataStr = line.slice(5).trim();
        if (!dataStr) continue;

        let evt;
        try {
          evt = JSON.parse(dataStr);
        } catch {
          continue;
        }

        if (evt.type === 'session' && evt.session_id) {
          onSessionId?.(evt.session_id);
        } else if (evt.type === 'token' && typeof evt.token === 'string') {
          onToken?.(evt.token);
        } else if (evt.type === 'done') {
          doneReceived = true;
        } else if (evt.type === 'error') {
          throw new Error(evt.detail || 'Chatbot error');
        }
      }

      if (doneReceived) {
        try {
          await reader.cancel();
        } catch {}
        break;
      }
    }

    onDone?.();
  } catch (error) {
    if (error?.name === 'AbortError') return;
    onError?.(error);
  }
};

// Fallback parser for non-streaming (full-text) SSE responses.
function parseSseChunk(raw, { onToken, onSessionId }) {
  const lines = raw.split('\n');
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line.startsWith('data:')) continue;
    const dataStr = line.slice(5).trim();
    if (!dataStr) continue;
    try {
      const evt = JSON.parse(dataStr);
      if (evt.type === 'session' && evt.session_id) {
        onSessionId?.(evt.session_id);
      } else if (evt.type === 'token' && typeof evt.token === 'string') {
        onToken?.(evt.token);
      }
    } catch {
      // ignore non-json lines
    }
  }
}
